import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/integrations/aws-kms`;
const options = { withCredentials: true, timeout: 42000 };

export const useKmsStatus = () => {
  const [data, setData] = useState(null), [error, setError] = useState('');
  const [loading, setLoading] = useState(true), [checking, setChecking] = useState(false);
  const [now, setNow] = useState(Date.now()), [receivedAt, setReceivedAt] = useState(Date.now());
  const [rateUntil, setRateUntil] = useState(0);
  const busy = useRef(false), mounted = useRef(true), sequence = useRef(0);
  const load = useCallback(async () => {
    if (busy.current) return;
    const turn = ++sequence.current;
    setLoading(true); setError('');
    try {
      const response = await axios.get(`${API}/status`, options);
      if (!mounted.current || turn !== sequence.current) return;
      setData(response.data); setReceivedAt(Date.now()); setNow(Date.now()); setRateUntil(0);
    } catch {
      if (mounted.current && turn === sequence.current) { setData(null); setError('error'); }
    } finally {
      if (mounted.current && turn === sequence.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true; load();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    const focus = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', focus);
    return () => { mounted.current = false; ++sequence.current; clearInterval(clock); window.removeEventListener('focus', focus); };
  }, [load]);
  useEffect(() => {
    if (error === 'rate' && now >= rateUntil) setError('');
  }, [error, now, rateUntil]);
  const check = async (success) => {
    if (busy.current) return;
    busy.current = true; ++sequence.current;
    setChecking(true); setLoading(false); setError('');
    try {
      const response = await axios.post(`${API}/check`, {}, options);
      if (!mounted.current) return;
      setData(response.data); setReceivedAt(Date.now()); setNow(Date.now()); setRateUntil(0);
      if (response.data.result === 'verified') toast.success(success);
    } catch (failure) {
      if (mounted.current) {
        if (failure.response?.status === 429) {
          const header = failure.response.headers?.['retry-after'];
          const numeric = header ? Number(header) : NaN;
          const seconds = Number.isFinite(numeric) ? numeric : header ? (Date.parse(header) - Date.now()) / 1000 : NaN;
          const wait = Number.isFinite(seconds) ? Math.max(1, Math.min(3600, Math.ceil(seconds))) : 45;
          const at = Date.now();
          setReceivedAt(at); setNow(at); setRateUntil(at + wait * 1000); setError('rate');
          setData(previous => previous ? { ...previous, result: 'not_checked', checked_at: null,
            identity_verified: false, key_verified: false, public_key_verified: false, retry_after_seconds: wait } : null);
        } else { setData(null); setRateUntil(0); setError('error'); }
      }
    } finally {
      busy.current = false;
      if (mounted.current) setChecking(false);
    }
  };
  const secondsLeft = data?.session_expires_at ? (new Date(data.session_expires_at).getTime() - now) / 1000 : null;
  const configuration = secondsLeft !== null && secondsLeft <= 0 ? 'expired' : secondsLeft !== null && secondsLeft <= 60 ? 'expiring' : data?.configuration;
  const stale = data?.checked_at && (now - new Date(data.checked_at).getTime()) > 300000;
  const result = stale ? 'stale' : data?.result;
  const retry = Math.max(0, Math.ceil((rateUntil - now) / 1000), (data?.retry_after_seconds || 0) - Math.floor((now - receivedAt) / 1000));
  const key = configuration && configuration !== 'ready' ? configuration : result;
  const canCheck = !!data && configuration === 'ready' && result !== 'session_invalid' && !retry && !loading && !checking && !error;
  return { data, error, loading, checking, retry, key, canCheck, load, check,
    flagsCurrent: configuration === 'ready' && !stale && !loading && !checking && !error };
};